// web/src/tutorial/quickstart/beat-bloom.ts
// Beat Bloom · Quick Start（最短流程：选鼓手 → 选 MIDI → 生成动画）
//
// 这一篇只讲「用默认值最快跑通一次」，参数细节一律放在
// 「软件界面详细说明」(/details/beat-bloom.html) 里，不在这里展开。
//
// 依据：H:\beat_bloom_rust
//   src/ui/app.rs（五个页签与顶栏）、avatar_panel.rs / display_avatar_image.rs /
//   drummer_editor.rs / drum_kit_ui.rs（角色设置）、midi_panel.rs（MIDI 处理）、
//   execution_panel.rs（执行与产物路径）、midi_merger_panel.rs（音频转 MIDI）、
//   recorder_editor.rs（Recorder 编辑器）、data.rs（默认值）、
//   asset/i18n/zh-CN.json 与 en-US.json（界面原文）

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const beatBloomQuickstart: TutorialDoc = {
  key: "beat_bloom",
  slug: "beat-bloom",
  kind: "quickstart",
  icon: "💥",
  title: l("Beat Bloom 鼓手演奏动画", "Beat Bloom Drummer Performance"),
  subtitle: l(
    "最短流程：选一个鼓手、选一份 MIDI、点一次生成，然后在 Blender / Unreal 里加载结果。这一篇全程用默认参数；每个参数管什么、什么时候该调，见[软件界面详细说明](/details/beat-bloom.html)。",
    "The short path: pick a drummer, pick a MIDI file, press generate once, then load the result in Blender / Unreal. Everything here runs on the defaults; for what each control does and when to touch it, see the [UI Reference](/details/beat-bloom.html).",
  ),
  readMinutes: 6,
  video: {
    url: "https://www.bilibili.com/video/BV1WsSqBvEzM/",
    youtube: "https://www.youtube.com/watch?v=6al2rxCNWCk",
    caption: l(
      "Beat Bloom 完整操作演示：2.beatBloom使用演示（B 站）",
      "Beat Bloom full walkthrough: Drummer In Unreal Engine: Part 0 Introduce (YouTube)",
    ),
    series: [
      { title: l("1.beatBloom介绍视频", "1.beatBloom介绍视频"), url: "https://www.bilibili.com/video/BV1KnSqBMEHV/" },
      { title: l("在Unreal中玩架子鼓-BeatBloomUnreal跑通", "在Unreal中玩架子鼓-BeatBloomUnreal跑通"), url: "https://www.bilibili.com/video/BV1aJwgzTEsD/" },
      { title: l("Drummer In Unreal Engine Part 1: Drum Kit Info", "Drummer In Unreal Engine Part 1: Drum Kit Info"), youtube: "https://www.youtube.com/watch?v=Y2zJTotbNRc" },
      { title: l("Drummer In Unreal Engine Part 2: Make Shape Key", "Drummer In Unreal Engine Part 2: Make Shape Key"), youtube: "https://www.youtube.com/watch?v=xW8A3cRViYA" },
      { title: l("Drummer In Unreal Engine Part 4: Setting Control Rig", "Drummer In Unreal Engine Part 4: Setting Control Rig"), youtube: "https://www.youtube.com/watch?v=b6nGcIHoAXM" },
      { title: l("Drummer In Unreal Engine Part 6: Generate Animation", "Drummer In Unreal Engine Part 6: Generate Animation"), youtube: "https://www.youtube.com/watch?v=XLV5YC4VTLE" },
    ],
  },
  images: [
    {
      src: "/assets/image/products/beat-bloom/CN/角色设置.png",
      srcEn: "/assets/image/products/beat-bloom/EN/Character Setting.png",
      label: l("角色设置", "Character Setting"),
      width: 1920,
      height: 1040,
    },
    {
      src: "/assets/image/products/beat-bloom/CN/MIDI处理.png",
      srcEn: "/assets/image/products/beat-bloom/EN/MIDI Processing.png",
      label: l("MIDI 处理", "MIDI Processing"),
      width: 902,
      height: 875,
    },
    {
      src: "/assets/image/products/beat-bloom/CN/执行操作.png",
      srcEn: "/assets/image/products/beat-bloom/EN/Execute.png",
      label: l("执行操作", "Execute"),
      width: 902,
      height: 875,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "overview",
      title: l("这一篇要做什么", "What you will do"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "Beat Bloom 读一份 MIDI 和一个自带鼓组映射的鼓手，把音高翻译成鼓件，逐帧算出「哪条肢体打哪个鼓」，最后导出一份插件能直接加载的结果文件。你不需要在 3D 软件里手动摆任何一帧。",
            "Beat Bloom reads a MIDI file and a drummer that carries its own drum-kit mapping, turns pitches into drum pieces, works out frame by frame which limb hits which piece, and exports a file the plugin loads directly. You never hand-key a single frame in the 3D app.",
          ),
        },
        {
          kind: "steps",
          items: [
            {
              title: l("选一个鼓手", "Pick a drummer"),
              text: l(
                "在 `⚙ 角色设置` 的下拉菜单里选一个。鼓手是软件自带的，不用你准备。",
                "Choose one from the dropdown on `⚙ Character Settings`. Drummers ship with the app — nothing to prepare.",
              ),
            },
            {
              title: l("选一份 MIDI", "Pick a MIDI file"),
              text: l(
                "在 `🎵 MIDI 处理` 里选文件、扫描、勾上要生成动画的轨道。",
                "On `🎵 MIDI Processing`, load the file, scan it, and tick the track you want animated.",
              ),
            },
            {
              title: l("点一次生成", "Press generate once"),
              text: l(
                "在 `▶ 执行操作` 点 `⚡ 一键生成（MIDI → Recorder → 动画）`，等控制台跑完，拿 `.beatbloom` 去插件里加载。",
                "On `▶ Execute`, press `⚡ Generate All (MIDI → Recorder → Animation)`, wait for the console to finish, then load the `.beatbloom` file in the plugin.",
              ),
            },
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "第一次使用，建议用软件自带的示例鼓手 + 一首只有鼓轨的简单 MIDI 跑通全流程，确认鼓组映射与输出目录都正常，再换成自己的素材。",
            "For your first run, use a bundled example drummer plus a simple MIDI that contains only a drum track. Once the mapping and the output folder behave, switch to your own material.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "prepare",
      title: l("开始之前", "Before you start"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "只需要准备一份 MIDI 文件。鼓手的鼓组映射、击打姿势这些数据都由软件自带，不用你操心。",
            "You only need a MIDI file. Each drummer's kit mapping and hit poses ship with the app, so there is nothing to set up.",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "**一份 MIDI 文件**（`.mid` / `.midi`）。鼓的部分最好单独成轨，但不确认也没关系：`🎵 MIDI 处理` 会把这份文件里所有含音符的轨道都列出来给你挑。",
              "**A MIDI file** (`.mid` / `.midi`). Keeping the drum part on its own track is best, but you do not have to know which one it is: `🎵 MIDI Processing` lists every track that contains notes.",
            ),
            l(
              "**只有音频、没有 MIDI 时**（MP3 / WAV），先用 `🎼 音频转MIDI` 把素材合并成一条打击乐轨 MIDI，再回到下面的主流程。",
              "**If you only have audio** (MP3 / WAV), use `🎼 Audio to MIDI` first to merge the clips into one percussion-track MIDI, then come back to the main flow below.",
            ),
          ],
        },
        {
          kind: "note",
          text: l(
            "演奏者不需要你准备。要把自己的人物做成可用角色（标定骨骼、鼓件位置与击打姿势），见《自定义人物进阶教程》。",
            "Performers are not something you prepare. Turning your own character into a usable performer — rig, piece positions and hit poses — is covered by the Custom Performer tutorial.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "ui-overview",
      title: l("界面总览：五个页签", "UI overview: five tabs"),
      blocks: [
        {
          kind: "p",
          text: l(
            "顶栏一共五个页签，前三个就是这次要走的主流程，顺序也是从左往右。",
            "The top bar has five tabs. The first three are the main flow, and they run left to right.",
          ),
        },
        {
          kind: "table",
          head: [
            l("页签", "Tab"),
            l("作用", "Purpose"),
            l("这次要不要用", "Needed now?"),
          ],
          rows: [
            [
              l("`⚙ 角色设置`", "`⚙ Character Settings`"),
              l(
                "选鼓手、看角色预览、改 `Head_Control 阈值`，以及编辑整份鼓组。",
                "Pick a drummer, check the preview, set the `Head_Control Threshold`, and edit the whole kit.",
              ),
              l("要，第 1 步", "Yes — step 1"),
            ],
            [
              l("`🎵 MIDI 处理`", "`🎵 MIDI Processing`"),
              l(
                "加载 MIDI 文件（支持拖放）、扫描轨道、勾选音轨。",
                "Load the MIDI file (drag and drop works), scan its tracks, tick the ones to animate.",
              ),
              l("要，第 2 步", "Yes — step 2"),
            ],
            [
              l("`▶ 执行操作`", "`▶ Execute`"),
              l(
                "回显当前鼓手与 MIDI 配置，运行生成，右侧控制台输出全过程日志。",
                "Echoes the current drummer and MIDI setup, runs generation, and streams the log to the console.",
              ),
              l("要，第 3 步", "Yes — step 3"),
            ],
            [
              l("`🎼 音频转MIDI`", "`🎼 Audio to MIDI`"),
              l(
                "把多个 MP3 / WAV 素材按打击乐类型起音检测，合并成一条打击乐轨的 MIDI 文件。",
                "Onset-detects MP3 / WAV clips per percussion type and merges them into one percussion-track MIDI file.",
              ),
              l("可选（只有音频时用）", "Optional (audio-only material)"),
            ],
            [
              l("`📝 Recorder 编辑器`", "`📝 Recorder Editor`"),
              l(
                "浏览并手工修正某几帧的「肢体 ↔ 鼓件」分配，改动就地覆盖 `.recorder` 文件。",
                "Browse and hand-fix the limb ↔ piece assignment for individual frames, overwriting the `.recorder` file in place.",
              ),
              l("可选（微调动作时用）", "Optional (fine-tuning)"),
            ],
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          title: l("顺序不能颠倒", "Do not reorder"),
          text: l(
            "执行阶段同时需要「鼓手的鼓组映射」和「MIDI 的轨道」，所以两者都必须先设好。`▶ 执行操作` 只有执行能力：它上面两张卡片是纯回显，没有任何输入框。",
            "Generation needs both the drummer's kit mapping and the MIDI tracks, so both must be set first. `▶ Execute` can only run: its top two cards are read-only echoes with no inputs.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "performer",
      title: l("第 1 步：选鼓手", "Step 1 — Pick a drummer"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `⚙ 角色设置`。左栏「角色选择」选人，右栏「角色预览」确认人对了，页面下半部分的「鼓组信息」是这份鼓组里有哪些鼓件。",
            "Open `⚙ Character Settings`. Pick the drummer in “Character Select” on the left, confirm the picture in “Character Preview” on the right; the “Drum Kit Info” block below lists the pieces in that kit.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "打开左栏 `角色选择` 卡片里的下拉菜单，点一个鼓手。程序会立刻读取它的鼓组配置，刷新「可用肢体」与下方的「鼓组信息」。",
              "Open the dropdown in the “Character Select” card and click a drummer. The app immediately reads that kit profile and refreshes “Available Limbs” and “Drum Kit Info”.",
            ),
            l(
              "核对「可用肢体」里的 chip（软件自带的鼓手是 `left_hand` / `right_hand` / `left_foot` / `right_foot`）——这四条就是后面能被分配去打鼓的全部肢体。",
              "Check the chips under “Available Limbs” (bundled drummers show `left_hand` / `right_hand` / `left_foot` / `right_foot`) — these are exactly the limbs the solver may assign to hit drums.",
            ),
            l(
              "看一眼右栏「角色预览」，确认图片是这个人。软件自带的鼓手通常用默认头像，换成你自己的图不会影响生成。",
              "Glance at “Character Preview” to confirm the picture matches. Bundled drummers usually keep the default avatar; swapping it does not affect generation.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: l("默认值就够用", "The defaults are enough"),
          text: l(
            "这一页唯一需要留意的数值是 `Head_Control 阈值`，默认 100% 已经是最灵敏的一档；下半部分的「鼓组信息」是给自定义鼓组用的，只跑流程时不用动。想逐个参数了解，见[软件界面详细说明](/details/beat-bloom.html)。",
            "The only number worth noticing here is the `Head_Control Threshold`; its default of 100% is already the most sensitive setting. The “Drum Kit Info” block below is for custom kits — leave it alone while you are only running the flow. To go through the parameters one by one, see the [UI Reference](/details/beat-bloom.html).",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "midi",
      title: l("第 2 步：选 MIDI 与轨道", "Step 2 — Load the MIDI and pick tracks"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `🎵 MIDI 处理`。左栏选文件，右栏勾轨道，两件事都做完再往下走。",
            "Open `🎵 MIDI Processing`. Load the file on the left, tick tracks on the right; finish both before moving on.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "点 `📂 选择 MIDI 文件`，或者把文件直接拖到卡片下方的 `📥 将 MIDI 文件拖放到此处` 虚线区域。",
              "Click `📂 Select MIDI File`, or drag the file onto the dashed `📥 Drop a MIDI file here` area lower in the card.",
            ),
            l(
              "点 `🔍 扫描 MIDI 信息`。右栏「轨道选择」会列出这份 MIDI 里**所有含音符的轨道**（一个音符都没有的轨不会出现）。",
              "Click `🔍 Scan MIDI Info`. “Track Selection” on the right lists **every track that contains notes** (tracks with none never appear).",
            ),
            l(
              "勾上要生成动画的那条轨。鼓手动画通常只勾鼓轨；拿不准就看 `乐器` 和 `音符数` 两列——打击乐轨的乐器名常是 `Unknown instrument`，音符数通常明显偏多。",
              "Tick the track to animate. For a drummer that is normally just the drum track; if unsure, read the `Instrument` and `Notes` columns — a percussion track often shows `Unknown instrument` and a clearly larger note count.",
            ),
            l(
              "点 `✅ 添加选中的轨道`。左栏「参数设置」下方的「已选择的轨道:」会出现你选的轨道号，这才算真的选上了。",
              "Click `✅ Add Selected Tracks`. The `Selected tracks:` chips under “Parameter Settings” on the left now show your track numbers — that is what actually counts.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          text: l(
            "勾选本身不生效。`✅ 添加选中的轨道` 会**先清空原有选择**再写入本次勾选，所以一个都没勾就点，会把选择清成「无轨道」，执行阶段就无从下手了。",
            "Ticking alone does nothing. `✅ Add Selected Tracks` **clears the previous selection first**, so clicking it with nothing ticked empties the selection to “No tracks” and leaves generation with nothing to work on.",
          ),
        },
        {
          kind: "note",
          text: l(
            "左栏 `参数设置` 里的 `使用通道:` / `帧率:` / `音符量化:` 保持默认（-1 / 60 / 无量化）就能跑通。只有当你目标工程的帧率不是 60 时才需要改 `帧率:`，改完必须重新生成一次。",
            "Leave `Use channel:` / `FPS:` / `Note quantization:` in “Parameter Settings” at their defaults (-1 / 60 / No quantization) and the flow works. Only change `FPS:` when your target project does not run at 60 fps — and regenerate afterwards.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "execute",
      title: l("第 3 步：生成动画", "Step 3 — Generate"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `▶ 执行操作`。左栏上面两张卡片是只读回显，用来在点按钮前核对「谁在打、打的是哪份文件、哪几条轨」。",
            "Open `▶ Execute`. The top two cards on the left are read-only echoes: use them to check who is playing, which file, and which tracks before you press anything.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "确认「角色概览」显示的是你要的鼓手（不是「未选择角色」），「MIDI 参数」里的 `轨道:` 是你刚勾的轨道号。",
              "Confirm “Character Overview” shows your drummer (not “No character selected”) and that `Track:` under “MIDI Parameters” lists the tracks you ticked.",
            ),
            l(
              "点 `⚡ 一键生成（MIDI → Recorder → 动画）`。最耗时的是逐帧搜索，素材长的时候要等一会儿。",
              "Press `⚡ Generate All (MIDI → Recorder → Animation)`. The frame-by-frame search is the slow part; on longer material it takes a while.",
            ),
            l(
              "在右栏「控制台输出」等到 `执行完成!`，并确认出现「所有notes都已成功映射」，或者看清「未映射音符统计:」列的是哪些音高。",
              "Wait in “Console Output” on the right until `Execution complete!` appears, and check for “All notes mapped successfully” — or read which pitches “Unmapped note statistics:” lists.",
            ),
            l(
              "点 `📋 复制结果`，它会把最后生成的 `.beatbloom` 路径复制到剪贴板，粘贴出来就能拿去加载。",
              "Click `📋 Copy Result`; it copies the path of the last generated `.beatbloom` to the clipboard, ready to paste.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: l("跑起来之后按钮会变", "The buttons change while it runs"),
          text: l(
            "生成期间两个按钮会替换成一个 `🔄 执行中...`，点它没有任何反应——程序里没有取消功能。判断还在不在跑：看按钮是否变回两个，或者看日志还在不在增长。",
            "While a run is going, both buttons are replaced by a single `🔄 Running...` button that does nothing when clicked — there is no cancel. To tell whether it is still working, watch for the two buttons to come back or the log to keep growing.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "fine-tuning",
      title: l("想细调参数？看详细说明", "Want to tune the parameters?"),
      blocks: [
        {
          kind: "p",
          text: l(
            "这一篇全程用默认值。软件界面上的每一个参数——`Head_Control 阈值`、`帧率:`、`音符量化:`、鼓组里的 `MIDI 触发器` 与 `可驱动肢体`、音频转 MIDI 的 `🔇 噪声过滤` 与 `🎚 灵敏度`，以及用来手工改某几帧的 `Recorder 编辑器`——都在[软件界面详细说明](/details/beat-bloom.html)里逐个讲清楚：管什么、什么时候调、调了会变成什么样。",
            "This page runs entirely on the defaults. Every control in the app — `Head_Control Threshold`, `FPS:`, `Note quantization:`, the `MIDI Triggers` and `Drivable Limbs` of a drum piece, `🔇 Noise Filter` and `🎚 Sensitivity` on Audio to MIDI, and the `Recorder Editor` for hand-fixing individual frames — is covered one by one in the [UI Reference](/details/beat-bloom.html): what it controls, when to change it, and what changes when you do.",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "动作太忙、结果文件太大 → 把 `Head_Control 阈值` 往下调。",
              "Motion too busy or the output file too large → lower the `Head_Control Threshold`.",
            ),
            l(
              "动画长度或节奏和工程对不上 → 检查 `帧率:` 是否与目标工程一致，然后重新生成。",
              "Animation length or timing does not line up → check that `FPS:` matches the target project, then regenerate.",
            ),
            l(
              "某些音完全没产生动作 → 看控制台的「未映射音符统计:」，再到「鼓组信息」给对应鼓件补 `MIDI 触发器`。",
              "Some notes produce no motion at all → read “Unmapped note statistics:” in the console, then add the pitches to the right piece under “Drum Kit Info” with `Add Trigger`.",
            ),
            l(
              "个别帧的手不顺 → 用 `📝 Recorder 编辑器` 改那几帧，再点 `🎬 生成动画（Recorder → 动画）` 只重算动画。",
              "A few frames read awkwardly → fix them in `📝 Recorder Editor`, then press `🎬 Generate Animation (Recorder → Animation)` to recompute animation only.",
            ),
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "result",
      title: l("结果在哪、怎么加载", "Where the result is and how to load it"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "生成结果写在程序自己的 `output/` 目录下。插件真正要加载的是 `output/final_result/{鼓手名}_{MIDI名}_{轨道}.beatbloom`；它只是一个指针文件，里面记着另外两个 `.animation` 文件的完整路径。这三个文件要放在一起。",
            "The results land in the app's own `output/` folder. What the plugin loads is `output/final_result/{drummer}_{midi}_{tracks}.beatbloom` — a small pointer file holding the absolute paths of two `.animation` files. Keep those three files together.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "在 Blender 或 Unreal 里启用免费的 MusicDoll 插件（发行目录 `beat_bloom_dist/` 里带了一个 Blender 插件包 `beat_bloom_addon.zip`）。",
              "Enable the free MusicDoll plugin in Blender or Unreal (the `beat_bloom_dist/` folder ships a Blender add-on package, `beat_bloom_addon.zip`).",
            ),
            l(
              "在插件面板里选择与生成时**同一个**鼓手。",
              "In the plugin panel, select the **same** drummer you used at generation time.",
            ),
            l(
              "加载这份 `.beatbloom` 文件。",
              "Load the `.beatbloom` file.",
            ),
            l(
              "插件读它指向的 `.animation` 与 `_shapekey.animation`，把四肢关键帧和鼓件形状键写回时间线，之后就可以播放或渲染。",
              "The plugin reads the `.animation` and `_shapekey.animation` it points at, writes the limb keyframes and piece shape keys onto the timeline, and you can play or render.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          title: l("加载前别挪动 output 目录", "Do not move the output folder first"),
          text: l(
            "`.beatbloom` 里记的是绝对路径。把 `output/` 改名、搬到别的盘或复制到另一台机器，插件就找不到数据了——这时重新生成一遍即可。",
            "The `.beatbloom` file stores absolute paths. Rename `output/`, move it to another drive, or copy it to another machine and the plugin can no longer find the data — just regenerate.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "checklist",
      title: l("跑通前的自检清单", "Pre-flight checklist"),
      blocks: [
        {
          kind: "table",
          head: [
            l("检查项", "Check"),
            l("在哪儿看", "Where to look"),
            l("期望看到什么", "What you want to see"),
          ],
          rows: [
            [
              l("鼓手已选", "A drummer is selected"),
              l("`▶ 执行操作` 的「角色概览」", "“Character Overview” on `▶ Execute`"),
              l(
                "显示鼓手名字，而不是「未选择角色」。",
                "The drummer's name, not “No character selected”.",
              ),
            ],
            [
              l("轨道真的加进去了", "The track really is added"),
              l("`▶ 执行操作` 的「MIDI 参数」", "“MIDI Parameters” on `▶ Execute`"),
              l(
                "`轨道:` 显示你勾的号（例如 `[11]`），而不是默认的 `[0]`。",
                "`Track:` shows your track (say `[11]`), not the default `[0]`.",
              ),
            ],
            [
              l("帧率与目标工程一致", "FPS matches the target project"),
              l("`🎵 MIDI 处理` 的 `帧率:`", "`FPS:` on `🎵 MIDI Processing`"),
              l(
                "与 Blender / Unreal 工程的帧率一致（常用 30 或 60）。",
                "The same as your Blender / Unreal project (30 or 60 are common).",
              ),
            ],
            [
              l("音高都被鼓组认领", "The kit claims every pitch"),
              l("右栏「控制台输出」", "“Console Output” on the right"),
              l(
                "出现「所有notes都已成功映射」；只有个别音高落在「未映射音符统计:」里通常不影响整体效果。",
                "“All notes mapped successfully”. A pitch or two under “Unmapped note statistics:” usually makes no visible difference.",
              ),
            ],
            [
              l("输出目录可写", "The output folder is writable"),
              l("程序目录下的 `output/`", "`output/` next to the app"),
              l(
                "生成后出现 `output/final_result/*.beatbloom`。",
                "A `output/final_result/*.beatbloom` file exists after the run.",
              ),
            ],
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "清单里最省事的一条：先用自带示例鼓手跑通，再替换成自己的素材。这样一旦出问题，你能确定是素材的问题，而不是环境或配置的问题。",
            "The cheapest habit of all: get through one run with a bundled drummer first, then swap in your own material. If something then breaks, you know it is the material rather than the setup.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("常见问题", "FAQ"),
      blocks: [
        {
          kind: "p",
          text: l(
            "**生成会改到我的 MIDI 文件吗？** 不会。主流程只读 MIDI；`🎼 音频转MIDI` 也是另存为新的 `.mid`。程序里唯一会就地覆盖文件的是 `📝 Recorder 编辑器` 的 `确认修改并覆盖`，它改的是 `output/recorder/` 下的 `.recorder`。",
            "**Will generation modify my MIDI file?** No. The main flow only reads MIDI, and `🎼 Audio to MIDI` saves a new `.mid`. The one place that overwrites a file in place is `Apply & Overwrite` in `📝 Recorder Editor`, and it touches the `.recorder` under `output/recorder/`.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**点了生成，控制台只多出一行「错误: 未选择角色」或「错误: 未选择MIDI文件」怎么办？** 这两条是执行前的检查：回 `⚙ 角色设置` 选一个鼓手、回 `🎵 MIDI 处理` 选一份 MIDI，再回来重新点。",
            "**I pressed generate and the console only says “Error: no character selected” or “Error: no MIDI file selected”.** Those are the two pre-run checks. Go back to `⚙ Character Settings` to select a drummer and `🎵 MIDI Processing` to load a file, then press generate again.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**点 `🎬 生成动画（Recorder → 动画）` 报 `Recorder 文件不存在` 怎么办？** 它按「当前鼓手 + 当前 MIDI + 当前轨道」去拼 `.recorder` 的路径。说明这个组合还没跑过一键生成，或者跑完之后换了其中任意一项。先跑一次 `⚡ 一键生成（MIDI → Recorder → 动画）`。",
            "**`🎬 Generate Animation (Recorder → Animation)` fails with “Recorder file does not exist”.** It builds the `.recorder` path from the current drummer, MIDI and tracks, so that exact combination has never had a full run — or one of the three changed afterwards. Run `⚡ Generate All (MIDI → Recorder → Animation)` once.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**我只有音频，没有鼓 MIDI。** 用 `🎼 音频转MIDI` 把素材按打击乐类型加进来，起音检测后合并成一条打击乐轨的 `.mid`，再回到 `🎵 MIDI 处理` 当普通 MIDI 用。",
            "**I only have audio, no drum MIDI.** Use `🎼 Audio to MIDI`: add the clips with their percussion types, let onset detection merge them into one percussion-track `.mid`, then treat it as any other MIDI back on `🎵 MIDI Processing`.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**个别帧的手不对，能改吗？** 能。用 `📝 Recorder 编辑器` 搜到那一帧，改肢体或鼓件，点 `确认修改并覆盖`，再回 `▶ 执行操作` 点 `🎬 生成动画（Recorder → 动画）` 重算动画——比整首重跑快得多。",
            "**A few frames look wrong — can I fix them?** Yes. Find them in `📝 Recorder Editor`, change the limb or the piece, click `Apply & Overwrite`, then press `🎬 Generate Animation (Recorder → Animation)` on `▶ Execute` to recompute animation only — far faster than a full run.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**生成很慢，结果文件也很大。** 最耗时的是逐帧搜索，结果大小则主要受 `帧率:` 与 `Head_Control 阈值` 影响。降低帧率、或把阈值往下调，都能减小关键帧数量。",
            "**Generation is slow and the result file is large.** The frame-by-frame search dominates the wait, while the size mostly follows `FPS:` and the `Head_Control Threshold`. A lower frame rate or a lower threshold cuts the number of keyframes.",
          ),
        },
      ],
    },
  ],
};
